import React, { useState, useEffect } from "react";
import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  XMarkIcon,
  PencilIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { ITenantContact } from "./RentedListing";
import {
  baseUrl,
  getTokenFromLocalStorage,
} from "./DataServices/Configuration";
import Loading from "./Loading";

interface ISecurityDepositReceiverModalProps {
  open: boolean;
  onClose: () => void;
  listingId: string;
  tenants: ITenantContact[];
  loading: boolean;
  onReceiverUpdated: () => Promise<void>;
}

interface EditingState {
  personId: string | null;
  selectedReceiverId: string | null;
}

export default function SecurityDepositReceiverModal({
  open,
  onClose,
  listingId,
  tenants,
  loading,
  onReceiverUpdated,
}: ISecurityDepositReceiverModalProps) {
  const [editing, setEditing] = useState<EditingState>({
    personId: null,
    selectedReceiverId: null,
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Auto-dismiss success message after 3.5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 3500);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleEditClick = (person: ITenantContact) => {
    // Who this person has selected as receiver
    const theirSelection = person.lastSelectedReceiverId || null;
    setEditing({
      personId: person.id,
      selectedReceiverId: theirSelection,
    });
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleCancelEdit = () => {
    setEditing({
      personId: null,
      selectedReceiverId: null,
    });
  };

  const handleUpdateReceiverForPerson = async (
    personId: string,
    newReceiverId: string,
  ) => {
    const person = tenants.find((t) => t.id === personId);
    const newReceiver = tenants.find((t) => t.id === newReceiverId);

    if (!person || !newReceiver) {
      setErrorMessage("Invalid selection");
      return;
    }

    setIsUpdating(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const token = await getTokenFromLocalStorage();

      const listingId = person.listingId;

      // Send simple request to backend endpoint
      const payload = {
        personId,
        newReceiverId,
        listingId,
      };

      console.log("Sending to backend:", payload);

      const response = await fetch(
        `${baseUrl}/api/Appointments/UpdateSecurityDepositReceiver`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const responseText = await response.text();
      console.log("Response status:", response.status);
      console.log("Response:", responseText);

      if (!response.ok) {
        throw new Error(
          `Failed to update receiver: ${responseText || response.status}`,
        );
      }

      setSuccessMessage(
        `✅ ${person.name}'s receiver updated to ${newReceiver.name}`,
      );

      // Refresh the data with a delay to ensure DB is updated
      setTimeout(async () => {
        console.log("Refreshing data...");
        setIsUpdating(false);
        try {
          await onReceiverUpdated();
          console.log("Data refreshed successfully!");
        } catch (err) {
          console.error("Error during refresh:", err);
        }
        setEditing({
          personId: null,
          selectedReceiverId: null,
        });
      }, 800);
    } catch (error: any) {
      console.error("Error updating receiver:", error);
      setErrorMessage(
        error.message || "Failed to update receiver. Please try again.",
      );
      setIsUpdating(false);
    }
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/20" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto pt-4 pb-4 sm:pt-6 sm:pb-6">
          <div className="flex items-center justify-center min-h-full px-3 sm:px-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl rounded bg-white shadow">
                {/* Header */}
                <div className="border-b bg-white px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between">
                  <Dialog.Title className="text-base sm:text-lg font-semibold text-gray-900">
                    Security Deposit Receiver
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    disabled={isUpdating}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-50 transition"
                  >
                    <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                  </button>
                </div>

                {/* Body */}
                <div className="px-4 py-4 sm:px-5 sm:py-5">
                  {loading ? (
                    <div className="flex justify-center py-10">
                      <Loading />
                    </div>
                  ) : tenants.length === 0 ? (
                    <div className="text-center py-10">
                      <p className="text-gray-600 text-sm">
                        No tenants available
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
                      {tenants.map((person) => {
                        const theirSelectedReceiverId =
                          person.lastSelectedReceiverId;
                        const theirSelectedReceiver = theirSelectedReceiverId
                          ? tenants.find(
                              (t) => t.id === theirSelectedReceiverId,
                            )
                          : null;
                        const selectedReceiverName =
                          theirSelectedReceiver?.name || "Not yet assigned";

                        const isEditingThisPerson =
                          editing.personId === person.id;

                        return (
                          <div
                            key={person.id}
                            className="border border-gray-200 rounded p-3 sm:p-4 bg-gray-50"
                          >
                            {/* Person Header */}
                            <div className="mb-3">
                              <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                                {person.name}
                              </h3>
                              <div className="flex flex-wrap gap-1 mt-1">
                                <span className="inline-block bg-gray-200 text-gray-800 text-xs px-2 py-0.5 rounded">
                                  {person.isGuarantor ? "Guarantor" : "Tenant"}
                                </span>
                                {person.isReceiver && (
                                  <span className="inline-block bg-gray-200 text-gray-800 text-xs px-2 py-0.5 rounded">
                                    Receiver
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Current Selection or Edit Mode */}
                            {!isEditingThisPerson ? (
                              <div className="bg-white border border-gray-200 rounded p-2.5 sm:p-3">
                                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
                                  Selected
                                </p>
                                <div className="flex items-center justify-between gap-2">
                                  <p className="text-gray-900 font-medium text-sm">
                                    {selectedReceiverName}
                                  </p>
                                  <button
                                    onClick={() => handleEditClick(person)}
                                    disabled={isUpdating}
                                    className="px-2 py-1 text-xs font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 transition flex-shrink-0"
                                  >
                                    Edit
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="bg-white border border-gray-200 rounded p-3 sm:p-4 space-y-2">
                                <p className="text-xs sm:text-sm font-semibold text-gray-900">
                                  Select receiver for{" "}
                                  <span className="text-gray-600">
                                    {person.name}
                                  </span>
                                </p>

                                <div className="space-y-1.5 max-h-40 overflow-y-auto pr-2">
                                  {tenants.map((option) => (
                                    <label
                                      key={option.id}
                                      className={`flex items-center gap-2 p-2 border rounded cursor-pointer transition text-xs sm:text-sm ${
                                        editing.selectedReceiverId === option.id
                                          ? "border-gray-400 bg-gray-100"
                                          : "border-gray-200 bg-white hover:border-gray-300"
                                      }`}
                                    >
                                      <input
                                        type="radio"
                                        name={`receiver-${person.id}`}
                                        value={option.id}
                                        checked={
                                          editing.selectedReceiverId ===
                                          option.id
                                        }
                                        onChange={() =>
                                          setEditing({
                                            personId: person.id,
                                            selectedReceiverId: option.id,
                                          })
                                        }
                                        className="h-3.5 w-3.5 text-gray-600 cursor-pointer flex-shrink-0"
                                      />
                                      <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-900">
                                          {option.name}
                                        </div>
                                        <div className="text-xs text-gray-600">
                                          {option.isGuarantor
                                            ? "Guarantor"
                                            : "Tenant"}
                                        </div>
                                      </div>
                                    </label>
                                  ))}
                                </div>

                                <div className="flex gap-1.5 pt-1.5">
                                  <button
                                    onClick={handleCancelEdit}
                                    disabled={isUpdating}
                                    className="flex-1 px-2 py-1.5 text-xs sm:text-sm font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 transition"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() =>
                                      editing.selectedReceiverId &&
                                      handleUpdateReceiverForPerson(
                                        person.id,
                                        editing.selectedReceiverId,
                                      )
                                    }
                                    disabled={
                                      isUpdating || !editing.selectedReceiverId
                                    }
                                    className="flex-1 px-2 py-1.5 text-xs sm:text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 transition"
                                  >
                                    {isUpdating ? "Saving..." : "Save"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Messages */}
                  {errorMessage && (
                    <div className="mt-3 p-3 bg-gray-100 border border-gray-300 rounded text-xs sm:text-sm text-gray-800">
                      <p className="font-medium mb-0.5">Error</p>
                      <p>{errorMessage}</p>
                    </div>
                  )}

                  {successMessage && (
                    <div className="mt-3 p-3 bg-gray-100 border border-gray-300 rounded text-xs sm:text-sm text-gray-800">
                      <p className="font-medium mb-0.5">Success</p>
                      <p>{successMessage}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t bg-gray-50 px-4 py-3 sm:px-5 sm:py-3 flex justify-end">
                  <button
                    onClick={onClose}
                    disabled={isUpdating}
                    className="px-3 py-1.5 text-xs sm:text-sm font-medium text-gray-700 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 transition"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
